import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatCheckboxField } from "blocks-nextjs/blocks-react/components/CheckboxField/CheckboxField";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconCalendarClock } from "blocks-nextjs/blocks-react/components/Icons/CalendarClock/CalendarClock";
import { CatIconShow } from "blocks-nextjs/blocks-react/components/Icons/Show/Show";
import { CatIconSyncronize } from "blocks-nextjs/blocks-react/components/Icons/Syncronize/Syncronize";
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import React, { useEffect, useRef, useState } from "react";
import Frame from "../../assets/Frame 627345.svg";
import { useDatePickerLiveViewConstraints } from "../../hooks/useDatePickerLiveViewConstraints";
import { useCreateChargerAssetLink } from "../../services/query/eflow-charger-asset-links.query";
import { isCatAdminUserEnergyFlowLinks } from "../../shared/validateUserPermission";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { formatExpiryDate, getMinDateForLiveView } from "../../utils/dateFormatter";
import DateRangeDropdown from "../DateRangeDropdown/DateRangeDropdown";
import SharedLinkToast from "../SharedList/SharedLinkToast";
import SharedList from "../SharedList/SharedList";
import CustomDatePickerPopover from "./CustomDatePickerPopover";
const MAX_LINK_NAME_LENGTH = 100;
const REFETCH_KEY_MULTIPLIER = 10000;
const DATE_RANGE_DEFAULT = "Last7Days";
const DATE_RANGE_CUSTOM = "CustomDate";
const FEATURE_OPTION_WRAPPER_CLASSES = "flex items-start space-x-3";
const FEATURE_ICON_ROW_CLASSES = "flex items-center space-x-2";
const FEATURE_ICON_CLASSES = "w-4 h-4";
const PRIMARY_BUTTON_CLASSES = "bg-yellow-400 text-black font-medium";
const LIVE_VIEW_MAX_DAYS_AHEAD = 1;
/**
 * Returns true when the given expiry date falls within the Live View Only allowed window
 * (today through today + LIVE_VIEW_MAX_DAYS_AHEAD, inclusive).
 */
function isDateWithinLiveViewRange(expiryDate) {
    try {
        let selectedDate;
        if (expiryDate.includes("/")) {
            const [month, day, year] = expiryDate.split("/").map(Number);
            selectedDate = new Date(year, month - 1, day);
        }
        else {
            selectedDate = new Date(expiryDate);
        }
        selectedDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + LIVE_VIEW_MAX_DAYS_AHEAD);
        maxDate.setHours(0, 0, 0, 0);
        return selectedDate >= today && selectedDate <= maxDate;
    }
    catch {
        return false;
    }
}
const GenerateLink = ({ isOpen, onClose, onCreateLink, refetchKey: externalRefetchKey }) => {
    const { userEntitlements, userPartyNumber } = useGlobalStore((state) => ({
        userEntitlements: state.userEntitlements,
        userPartyNumber: state.userPartyNumber
    }));
    const { assetDetails, projectMetaData } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData
    }));
    const dateRef = useRef(null);
    // Get timezone offset from project metadata for timezone-aware date formatting
    const timezoneOffset = projectMetaData?.offset || "UTC+00:00";
    const createLinkMutation = useCreateChargerAssetLink();
    const [showToast, setShowToast] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [internalRefetchKey, setInternalRefetchKey] = useState(0); // Internal key for link creation
    // Use external refetch key if provided, otherwise use internal
    const refetchKey = externalRefetchKey ?? internalRefetchKey;
    const [formData, setFormData] = useState({
        expiryDate: "",
        features: {
            canSeeHistory: false,
            canUseChargerControls: false,
            canUseSchedule: false,
            liveViewOnly: false
        },
        name: ""
    });
    const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE_DEFAULT);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    // Counter to force refetch when the same date range is selected again
    const [dateRangeSelectionCounter, setDateRangeSelectionCounter] = useState(0);
    // Combine refetchKey with dateRangeSelectionCounter to create a unique key
    const combinedRefetchKey = refetchKey * REFETCH_KEY_MULTIPLIER + dateRangeSelectionCounter;
    // Increment internal refetch key when GenerateLink opens (only if no external key provided)
    useEffect(() => {
        if (isOpen && externalRefetchKey === undefined) {
            setInternalRefetchKey((prev) => prev + 1);
        }
    }, [isOpen, externalRefetchKey]);
    // Apply Live View Only date constraints to the datepicker's internal calendar
    const isMaxTwoDays = formData.features.liveViewOnly;
    const isMaxThreeMonths = formData.features.canSeeHistory || formData.features.canUseChargerControls || formData.features.canUseSchedule;
    useDatePickerLiveViewConstraints(dateRef, isMaxTwoDays, isMaxThreeMonths, formData.expiryDate);
    const handleInputChange = (field, value) => {
        // Validate name field: max 100 characters, only alphanumeric, spaces, @, and -
        if (field === "name") {
            // Remove any characters that are not alphanumeric, space, @, or -
            const sanitizedValue = value.replace(/[^a-zA-Z0-9\s@-]/g, "");
            // Limit to max length
            const truncatedValue = sanitizedValue.slice(0, MAX_LINK_NAME_LENGTH);
            setFormData((prev) => ({
                ...prev,
                [field]: truncatedValue
            }));
        }
        else if (field === "expiryDate") {
            // Validate expiry date when Live View Only is active
            setFormData((prev) => {
                // If Live View Only is checked, validate the date is within 2 days
                if (prev.features.liveViewOnly && value) {
                    if (!isDateWithinLiveViewRange(value)) {
                        console.warn("Date exceeds Live View Only allowed range (today and tomorrow only), ignoring change");
                        return prev; // Don't update state
                    }
                }
                return {
                    ...prev,
                    [field]: value
                };
            });
        }
        else {
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));
        }
    };
    const handleFeatureChange = (feature, checked) => {
        setFormData((prev) => {
            const newFeatures = {
                ...prev.features,
                [feature]: checked
            };
            if (feature === "liveViewOnly" && checked) {
                newFeatures.canSeeHistory = false;
                newFeatures.canUseChargerControls = false;
                newFeatures.canUseSchedule = false;
                // Check if current expiry date exceeds the 2-day limit for Live View Only
                if (prev.expiryDate && !isDateWithinLiveViewRange(prev.expiryDate)) {
                    return {
                        expiryDate: "", // Clear the date
                        features: newFeatures,
                        name: prev.name
                    };
                }
            }
            // When checking a non-Live View Only feature
            if (feature !== "liveViewOnly" && checked) {
                // Disable Live View Only if it was previously checked
                if (prev.features.liveViewOnly) {
                    newFeatures.liveViewOnly = false;
                }
            }
            return {
                ...prev,
                features: newFeatures
            };
        });
    };
    const handleDateRangeChange = (value) => {
        setSelectedDateRange(value);
        if (value === DATE_RANGE_CUSTOM) {
            setShowCustomDatePicker(true);
        }
        else {
            setShowCustomDatePicker(false);
            // Increment counter to force refetch even when selecting the same date range
            setDateRangeSelectionCounter((prev) => prev + 1);
        }
    };
    const handleCustomDateApply = (startDate, endDate) => {
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
        setShowCustomDatePicker(false);
    };
    const handleCustomDateCancel = () => {
        setShowCustomDatePicker(false);
    };
    const handleCreateLink = async () => {
        setIsCreating(true);
        try {
            // Don't proceed if required IDs are missing
            if (!assetDetails?.assetId || !projectMetaData?.projectId || !userPartyNumber) {
                console.error("Cannot create link: Missing required assetId, projectId, or partyNumber");
                setIsCreating(false);
                return;
            }
            // Convert features object to array format for API
            const featuresArray = [];
            if (formData.features.liveViewOnly)
                featuresArray.push("live view only");
            if (formData.features.canSeeHistory)
                featuresArray.push("can see history");
            if (formData.features.canUseChargerControls)
                featuresArray.push("can use charger controls");
            if (formData.features.canUseSchedule)
                featuresArray.push("can use schedule");
            // Format expiry date to MM/dd/yyyy format with timezone offset
            const formattedExpiryDate = formatExpiryDate(formData.expiryDate, timezoneOffset);
            // Create the API request payload
            const requestBody = {
                assetId: assetDetails.assetId,
                expiryDate: formattedExpiryDate,
                features: featuresArray,
                name: formData.name,
                partyNumber: userPartyNumber,
                projectId: projectMetaData.projectId
            };
            // Call the API to create the link
            await createLinkMutation.mutateAsync(requestBody);
            // Show success toast
            setShowToast(true);
            // Increment refetch key to reset SharedList pagination to page 1
            // Only increment internal key if external key is not provided
            if (externalRefetchKey === undefined) {
                setInternalRefetchKey((prev) => prev + 1);
            }
            if (onCreateLink) {
                onCreateLink(formData);
            }
            // Reset form
            setFormData({
                expiryDate: "",
                features: {
                    canSeeHistory: false,
                    canUseChargerControls: false,
                    canUseSchedule: false,
                    liveViewOnly: false
                },
                name: ""
            });
        }
        catch (error) {
            console.error("Error creating charger asset link:", error);
            // need to handle error handling UI
        }
        finally {
            setIsCreating(false);
        }
    };
    const hasSelectedFeature = Object.values(formData.features).some((isEnabled) => isEnabled);
    const isFormValid = formData.name.trim() !== "" && formData.expiryDate.trim() !== "" && hasSelectedFeature;
    return (_jsxs("div", { className: "px-3 sm:px-4", "data-test": "generate-link-container", id: "generate-link-container", children: [_jsx(SharedLinkToast, { heading: "Link Generated Successfully", message: "Your link has been created successfully. It's ready to share!", onClose: () => setShowToast(false), show: showToast }), _jsxs("div", { className: "flex flex-col xl:flex-row gap-4 sm:gap-6 xl:gap-8", "data-test": "generate-link-form", children: [_jsxs("div", { className: "w-full xl:w-[55%]", children: [_jsxs("div", { className: "mb-4 sm:mb-6", children: [_jsx(CatHeading, { "data-test": "generate-link-title", variant: "title", children: "Generate Link" }), _jsx("p", { className: "text-gray-600 mt-2 text-xs sm:text-[12px]", "data-test": "generate-link-description", children: "Generate a custom shareable link with a chosen name, expiration date, and specific feature permissions such as viewing history using charger controls, or setting schedules." })] }), _jsxs("div", { className: "flex flex-col sm:flex-row gap-4 sm:gap-6", children: [_jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", "data-test": "link-name-label", children: ["Name ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx("div", { className: "h-12", children: _jsx(CatInputField, { className: "h-full custom-height-input", "data-test": "link-name-input", fieldId: "link-name", onBlChange: (e) => handleInputChange("name", e.detail?.value || ""), placeholder: "Enter link name", size: "sm", value: formData.name }) }), _jsxs("div", { className: "!text-[10px] text-gray-600 mt-1", children: [formData.name.length, "/", MAX_LINK_NAME_LENGTH, " characters. Allowed: letters, numbers, spaces, @ and -"] })] }), _jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", "data-test": "expire-date-label", children: ["Expire Date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx("div", { className: "relative h-12", children: _jsx(CatDatepickerField, { ariaDescribedBy: "metrics-datepicker-field-aria-1", className: "h-full custom-height-input", "data-test": "expire-date-picker", disabledMinDate: getMinDateForLiveView(), fieldId: "metrics-datepicker-field-1", onBlDatePickerChange: (e) => handleInputChange("expiryDate", e.detail?.value || ""), placeholder: "mm/dd/yyyy", ref: dateRef, size: "sm", value: formData.expiryDate }) }), formData.features.liveViewOnly && (_jsx("div", { className: "!text-[10px] text-gray-600", "data-test": "expire-date-hint", children: "For Live View Only, expiration date must be within today + next 1 day." }))] })] })] }), _jsx("div", { className: "w-full xl:w-[45%]", children: _jsxs("div", { "data-test": "features-section", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-4", "data-test": "features-label", children: ["Features ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: FEATURE_OPTION_WRAPPER_CLASSES, "data-test": "live-view-only-option", children: [_jsx(CatCheckboxField, { checked: formData.features.liveViewOnly, "data-test": "live-view-only-checkbox", disabled: formData.features.canSeeHistory ||
                                                        formData.features.canUseChargerControls ||
                                                        formData.features.canUseSchedule, fieldId: "live-view-only", onBlCheckboxChange: (e) => handleFeatureChange("liveViewOnly", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: FEATURE_ICON_ROW_CLASSES, children: [_jsx(CatIconShow, { className: FEATURE_ICON_CLASSES }), _jsx("span", { className: "font-medium", "data-test": "live-view-only-label", children: "Live View Only" })] }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Can be used without a CWS ID (disables others)" })] })] }), _jsxs("div", { className: FEATURE_OPTION_WRAPPER_CLASSES, "data-test": "can-see-history-option", children: [_jsx(CatCheckboxField, { checked: formData.features.canSeeHistory, "data-test": "can-see-history-checkbox", disabled: formData.features.liveViewOnly, fieldId: "can-see-history", onBlCheckboxChange: (e) => handleFeatureChange("canSeeHistory", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: FEATURE_ICON_ROW_CLASSES, children: [_jsx(CatIconSyncronize, { className: FEATURE_ICON_CLASSES }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, "data-test": "can-see-history-label", children: "Can see history" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Allows the user to view the history of charging sessions." })] })] }), _jsxs("div", { className: FEATURE_OPTION_WRAPPER_CLASSES, "data-test": "can-use-charger-controls-option", children: [_jsx(CatCheckboxField, { checked: formData.features.canUseChargerControls, "data-test": "can-use-charger-controls-checkbox", disabled: formData.features.liveViewOnly, fieldId: "can-use-charger-controls", onBlCheckboxChange: (e) => handleFeatureChange("canUseChargerControls", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: FEATURE_ICON_ROW_CLASSES, children: [_jsx("img", { alt: "charger controls", className: FEATURE_ICON_CLASSES, src: Frame }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, "data-test": "can-use-charger-controls-label", children: "Can use charger controls" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Lets the user manage charging by starting or stopping the charger." })] })] }), _jsxs("div", { className: FEATURE_OPTION_WRAPPER_CLASSES, "data-test": "can-use-schedule-option", children: [_jsx(CatCheckboxField, { checked: formData.features.canUseSchedule, "data-test": "can-use-schedule-checkbox", disabled: formData.features.liveViewOnly, fieldId: "can-use-schedule", onBlCheckboxChange: (e) => handleFeatureChange("canUseSchedule", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: FEATURE_ICON_ROW_CLASSES, children: [_jsx(CatIconCalendarClock, { className: FEATURE_ICON_CLASSES }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, "data-test": "can-use-schedule-label", children: "Can use schedule" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Lets user plan ahead by setting charging times." })] })] })] })] }) })] }), isCatAdminUserEnergyFlowLinks(userEntitlements) && (_jsx("div", { className: "mt-6 sm:mt-8 pt-4 sm:pt-6 border-b", children: _jsx(CatButton, { className: `${PRIMARY_BUTTON_CLASSES} mb-4 sm:mb-6 w-full sm:w-auto`, "data-test": "create-link-button", disabled: !isFormValid || isCreating, onClick: handleCreateLink, variant: "primary", children: isCreating ? "Creating Link..." : "Create Link" }) })), _jsxs("div", { "data-test": "shared-links-section", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4", children: [_jsx(CatHeading, { "data-test": "shared-links-title", variant: "title-sm", children: "Shared Links List" }), _jsx("div", { className: "flex items-center gap-4", children: _jsx(DateRangeDropdown, { "data-test": "shared-links-date-range", onValueChange: handleDateRangeChange, selectedValue: selectedDateRange }) })] }), _jsx(CustomDatePickerPopover, { "data-test": "custom-date-picker-popover", initialEndDate: customEndDate, initialStartDate: customStartDate, onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset }), _jsx("div", { className: "p-2 sm:p-4 min-h-[380px]", "data-test": "shared-links-list-container", children: _jsx(SharedList, { context: "shared-links-list", customEndDate: customEndDate, customStartDate: customStartDate, dateRange: selectedDateRange, dateRangeSelectionCounter: dateRangeSelectionCounter, refetchKey: combinedRefetchKey }) })] }), _jsx("div", { className: "flex justify-center sm:justify-end mt-12 sm:mt-20 mb-0", children: _jsx(CatButton, { className: `${PRIMARY_BUTTON_CLASSES} mb-4 w-full sm:w-auto`, "data-test": "done-button", onClick: onClose, variant: "primary", children: "Done" }) })] }));
};
export default GenerateLink;
